import React from 'react';
import { deleteAllCookies } from 'smart-react';

import { callApi } from '../../../Utils/Api/Api';
import { getTokensFromStorage } from '../../../Utils/Auth/AuthToken';
import {
  buildUserAuthObject,
  canAccessRoute as canAccessRouteWithUser,
  getStoredUserData,
  hasAllPermissions as userHasAllPermissions,
  hasAnyPermission as userHasAnyPermission,
  hasModuleAccess as userHasModuleAccess,
  hasPermission as userHasPermission,
  normalizeUserPayload,
  parseJwt,
  parseTokenExpirySeconds,
  storeUserData,
} from '../../../Utils/Auth/authorization';
import {
  ACCESS_TOKEN,
  REFRESH_TOKEN,
  TOKEN_EXP,
  ID_TOKEN,
} from '../../../constants/applicationConstants';
import { getConfigurationSetting } from '../../Settings/Services/SettingService';

export const AuthContext = React.createContext();

export const useAuth = () => React.useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = React.useState(() => getStoredUserData());
  const [userAuthObj, setUserAuthObj] = React.useState(() =>
    buildUserAuthObject(getStoredUserData())
  );
  const [configData, setConfigData] = React.useState(null);
  const [profileData] = React.useState(null);
  const [isLoading, setIsLoading] = React.useState(true);

  const setNormalizedUser = React.useCallback((userData) => {
    if (!userData) {
      setUser(null);
      setUserAuthObj(buildUserAuthObject(null));
      return null;
    }

    const normalizedUser = normalizeUserPayload(userData);
    setUser(normalizedUser);
    setUserAuthObj(buildUserAuthObject(normalizedUser));
    storeUserData(normalizedUser);

    return normalizedUser;
  }, []);

  const setAuthTokens = React.useCallback((tokens) => {
    if (!tokens) {
      return;
    }

    if (tokens.access_token) {
      sessionStorage.setItem(ACCESS_TOKEN, tokens.access_token);
    }
    if (tokens.refresh_token) {
      sessionStorage.setItem(REFRESH_TOKEN, tokens.refresh_token);
    }
    if (tokens.id_token) {
      sessionStorage.setItem(ID_TOKEN, tokens.id_token);
    }

    const tokenExpirySeconds = parseTokenExpirySeconds(tokens);
    if (tokenExpirySeconds) {
      sessionStorage.setItem(TOKEN_EXP, String(tokenExpirySeconds));
    } else {
      sessionStorage.removeItem(TOKEN_EXP);
    }
  }, []);

  const updateUser = React.useCallback(
    (userdt) => setNormalizedUser(userdt),
    [setNormalizedUser]
  );

  const completeLogin = React.useCallback(
    (payload) => {
      if (!payload) {
        return null;
      }

      if (payload.token) {
        setAuthTokens(payload.token);
      }

      return setNormalizedUser(payload);
    },
    [setAuthTokens, setNormalizedUser]
  );

  const checkTokens = React.useCallback(() => {
    const { accessToken, tokenExp } = getTokensFromStorage();

    if (!accessToken) {
      return false;
    }

    const accessTokenPayload = parseJwt(accessToken);
    const tokenExpiry = tokenExp || accessTokenPayload?.exp || null;

    if (!tokenExpiry) {
      return true;
    }

    return Date.now() < tokenExpiry * 1000;
  }, []);

  const login = React.useCallback(() => {
    window.location.replace('/login');
  }, []);

  const logout = React.useCallback(() => {
    setUser(null);
    setUserAuthObj(buildUserAuthObject(null));
    sessionStorage.clear();
    deleteAllCookies();
    window.location.replace('/login');
  }, []);

  const fetchConfiguration = React.useCallback(async () => {
    try {
      const response = await getConfigurationSetting();
      setConfigData(response?.config_data ?? null);
    } catch (error) {
      console.error('Error getting configuration setting:', error);
    }
  }, []);

  const hydrateCurrentUser = React.useCallback(async () => {
    if (!getTokensFromStorage().accessToken) {
      return null;
    }

    try {
      const response = await callApi('/user', 'GET');

      if (response?.isSuccess && response?.payload) {
        return setNormalizedUser(response.payload);
      }
    } catch (error) {
      console.error('Error loading current user:', error);
    }

    return null;
  }, [setNormalizedUser]);

  React.useEffect(() => {
    let isMounted = true;

    const bootstrapAuth = async () => {
      const { accessToken } = getTokensFromStorage();

      if (!accessToken) {
        if (isMounted) {
          setIsLoading(false);
        }
        return;
      }

      if (!checkTokens()) {
        if (isMounted) {
          setUser(null);
          setUserAuthObj(buildUserAuthObject(null));
          sessionStorage.clear();
          setIsLoading(false);
        }
        return;
      }

      const storedUser = getStoredUserData();
      if (storedUser && isMounted) {
        setUser(storedUser);
        setUserAuthObj(buildUserAuthObject(storedUser));
      }

      const hasStoredPermissions =
        (storedUser?.permission_names?.length ?? 0) > 0 ||
        (storedUser?.authorization?.permission_names?.length ?? 0) > 0;

      if (!hasStoredPermissions) {
        await hydrateCurrentUser();
      }

      if (!configData) {
        await fetchConfiguration();
      }

      if (isMounted) {
        setIsLoading(false);
      }
    };

    bootstrapAuth();

    return () => {
      isMounted = false;
    };
  }, [checkTokens, configData, fetchConfiguration, hydrateCurrentUser]);

  const hasPermission = React.useCallback(
    (permissionName) => userHasPermission(user || getStoredUserData(), permissionName),
    [user]
  );

  const hasAnyPermission = React.useCallback(
    (permissionNames) =>
      userHasAnyPermission(user || getStoredUserData(), permissionNames),
    [user]
  );

  const hasAllPermissions = React.useCallback(
    (permissionNames) =>
      userHasAllPermissions(user || getStoredUserData(), permissionNames),
    [user]
  );

  const hasModuleAccess = React.useCallback(
    (moduleName) => userHasModuleAccess(user || getStoredUserData(), moduleName),
    [user]
  );

  const canAccessRoute = React.useCallback(
    (route) => canAccessRouteWithUser(user || getStoredUserData(), route),
    [user]
  );

  const value = React.useMemo(
    () => ({
      logout,
      login,
      checkTokens,
      setAuthTokens,
      completeLogin,
      hydrateCurrentUser,
      user,
      userAuthObj,
      updateUser,
      getTokensFromStorage,
      configData,
      refreshConfiguration: fetchConfiguration,
      profileData,
      isLoading,
      hasPermission,
      hasAnyPermission,
      hasAllPermissions,
      hasModuleAccess,
      canAccessRoute,
    }),
    [
      canAccessRoute,
      checkTokens,
      completeLogin,
      configData,
      fetchConfiguration,
      hasAllPermissions,
      hasAnyPermission,
      hasModuleAccess,
      hasPermission,
      hydrateCurrentUser,
      isLoading,
      login,
      logout,
      profileData,
      setAuthTokens,
      updateUser,
      user,
      userAuthObj,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
